import { Fragment, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  crmCounts,
  crmFacilitySearch,
  crmGmailAccounts,
  crmProspects,
  fmtCount,
  salesKit,
  salesOutcomes,
  salesKitSearch,
  salesPackSend,
  type ApiError,
  type CatalogRow,
  type KitPeerBenchmark,
  type KitProspect,
  type SalesPackResult,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';
import { ErrorNote } from '../../../ErrorNote';
import { Skeleton } from '../../../ui/states';
import { useSales } from '../context';
import { ck, hov, sx } from '../dc';
import { buildKit, copyText, defaultHits, docText, itemOf, kitIndex, kitObjections, proofOf, sentences, sourceList, withPricingWords, type KitItem } from './model';
import { tc } from '../targeting/model';

type Mode = 'call' | 'obj' | 'send' | 'lib';
const MODES: [Mode, string][] = [
  ['call', 'In a call'],
  ['obj', 'Objections'],
  ['send', 'Send to a facility'],
  ['lib', 'Library'],
];
/** The "In a call" quick searches (design CHIPS). */
const CHIPS = ['What is the catch?', 'Cost', 'HIPAA', 'EHR', 'Staff', '911', 'Wearables', 'Reimbursement', 'Competitor'];
/** How many CRM priority facilities the Send picker filters (the design's PRIORITY list). */
export const PRIORITY_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 250;

// design salesVals helpers (L20038, L20546-20549)
const btn = (primary: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');
const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:5px;padding:2px 7px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';
const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;background:rgba(34,39,46,.05);');
const COPYON = 'background:rgba(47,138,111,.16);color:#12503C;';
const COPYOFF = 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);';
const cbtn = (on: boolean) =>
  'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (on ? COPYON : COPYOFF);
const qbtn = (on: boolean) =>
  'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;letter-spacing:.05em;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:rgba(47,138,111,.16);color:#12503C;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');
const enter = (i: number, step = 24, max = 240, dur = '.26s') => 'animation:riseIn ' + dur + ' cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(max, i * step) + 'ms;';
const chipStyle = (c: string) =>
  chip(c === 'CONFIDENTIAL' ? '#B3261E' : c === 'SHAREABLE' ? '#1F6B52' : '#54606C', c === 'CONFIDENTIAL' ? 'rgba(179,38,30,.14)' : c === 'SHAREABLE' ? 'rgba(47,138,111,.14)' : 'rgba(34,39,46,.08)');

/** "274,000+": the catalog's size, rounded down, for "search all ... in CRM". */
const roundedCount = (n: number) => fmtCount(Math.floor(n / 1000) * 1000) + '+';
const profileOf = (p: KitProspect) => [p.type, p.state, p.beds != null ? p.beds + ' beds' : null, 'Tier ' + p.tier].filter(Boolean).join(' · ');

/** The One-Pager's personalized fields from the catalog row (design kitPzFields). Missing data reads "not captured" or an en dash. */
export function pzFields(f: CatalogRow & { peer_benchmark?: KitPeerBenchmark | null }): { label: string; val: string }[] {
  const pb = f.peer_benchmark;
  return [
    ['Facility', f.name ?? '–'],
    ['Location', [f.city, f.state].filter(Boolean).join(', ') || '–'],
    ['Beds', f.beds != null ? String(f.beds) : '–'],
    ['Owner / admin', f.parent_operator ?? 'not captured'],
    ['EHR captured', f.ehr ?? 'not captured'],
    ['CMS rating', f.overall_stars != null ? f.overall_stars + '★ overall' : '–'],
    ['Aithora grade', f.grade ? f.grade + (f.score != null ? ' · ' + f.score.toFixed(0) + '/100 composite' : '') : '–'],
    [
      'State peer benchmark',
      pb ? 'occupancy ' + (pb.occupancy * 100).toFixed(0) + '% vs ' + (pb.state_median * 100).toFixed(0) + '% ' + (f.state ?? '') + ' median, n=' + pb.peers + ' peer group' : '–',
    ],
  ].map((r) => ({ label: r[0], val: r[1] }));
}

/** Shown under the Library until a pilot completes 90 days. */
export const PILOT_PROOF_EMPTY = 'Pilot results publish here as buildings complete 90 days';

/**
 * The Library's footer (Andrew, 2026-10-01): a pilot proof line from real Outcomes data,
 * MEASURED, from completed 90-day pilots (GET /sales/outcomes?mode=live pilot_proof).
 * Never our company's revenue (it replaced the Overview money line the design repeated).
 */
function usePilotProofLine(enabled: boolean): string {
  const { client, session } = useSession();
  const q = useQuery((signal) => salesOutcomes(client, { query: { mode: 'live' }, signal }), [client, session?.token], { enabled });
  if (!q.data) return '';
  const p = q.data.pilot_proof;
  return p?.line ? 'MEASURED · ' + p.line : PILOT_PROOF_EMPTY;
}

/**
 * Sales Kit (design T6190-6398, salesVals L20044-20257 and L20542-20683). The copy comes
 * from GET /sales/kit (sales_kit_sections; investor material is never served). Four
 * modes: In a call (search, /sales/kit/search), Objections, Send to a facility
 * (/crm/prospects, POST /sales/packs) and the Library (with the One-Pager's catalog
 * personalization through /crm/facilities/search).
 */
export function KitTab() {
  const { client, session } = useSession();
  const { params, setParam, openPrep, openDeal, setTab, canWrite, writeReason, userId } = useSales();
  const mode: Mode = (MODES.find((m) => m[0] === params.get('kitMode'))?.[0] ?? 'call') as Mode;

  const kitQ = useQuery((signal) => salesKit(client, { signal }), [client, session?.token]);
  const kit = useMemo(() => buildKit((kitQ.data?.sections ?? []).map(withPricingWords), kitQ.data?.docs), [kitQ.data]);
  const idx = useMemo(() => kitIndex(kit), [kit]);

  // ── In a call ──
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const qt = q.trim();
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(qt), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [qt]);
  const searchQ = useQuery((signal) => salesKitSearch(client, { query: { q: debounced }, signal }), [client, debounced], { enabled: mode === 'call' && !!debounced });
  const hits: KitItem[] = useMemo(() => {
    if (!qt) return defaultHits(idx).slice(0, 12);
    if (debounced !== qt || !searchQ.data || searchQ.loading) return [];
    return searchQ.data.results.map((r) => itemOf(withPricingWords(r))).slice(0, 12);
  }, [qt, debounced, idx, searchQ.data, searchQ.loading]);
  const searching = !!qt && (debounced !== qt || searchQ.loading);
  const copy = (key: string, text: string) => {
    copyText(text);
    setCopied(key);
  };

  // ── Objections ──
  const objs = useMemo(() => kitObjections(idx, kit), [idx, kit]);

  // ── Send to a facility ──
  const share = useMemo(() => kit.filter((d) => d.chip === 'SHAREABLE').concat(kit.filter((d) => d.personalize && d.chip !== 'SHAREABLE')), [kit]);
  const [packSel, setPackSel] = useState<Record<string, boolean> | null>(null);
  const defaultPack = useMemo(() => {
    const p: Record<string, boolean> = {};
    const first = kit.find((d) => d.chip === 'SHAREABLE');
    const pz = kit.find((d) => d.personalize);
    if (pz) p[pz.doc] = true;
    if (first) p[first.doc] = true;
    return p;
  }, [kit]);
  const sel = packSel ?? defaultPack;
  const nSel = share.filter((d) => sel[d.doc]).length;
  const [sendQ, setSendQ] = useState('');
  const [pick, setPick] = useState<KitProspect | null>(null);
  const [sendAns, setSendAns] = useState<{ facilityId: string; res?: SalesPackResult; err?: ApiError } | null>(null);
  const [sending, setSending] = useState(false);
  const prosQ = useQuery((signal) => crmProspects(client, { query: { limit: PRIORITY_LIMIT }, signal }), [client, session?.token], { enabled: mode === 'send' });
  const countsQ = useQuery((signal) => crmCounts(client, { signal }), [client, session?.token], { enabled: mode === 'send' });
  const mailQ = useQuery((signal) => crmGmailAccounts(client, { signal }), [client, session?.token], { enabled: mode === 'send' });
  const connected = !!mailQ.data?.accounts.some((a) => a.userId === userId && a.status === 'active');
  const pq = sendQ.trim().toLowerCase();
  const prospects = (prosQ.data?.prospects ?? []) as KitProspect[];
  const prHits = prospects.filter((p) => !pq || ((p.name ?? '') + ' ' + profileOf(p)).toLowerCase().includes(pq));
  const sent = !!pick && sendAns?.facilityId === pick.facilityId && sendAns.res?.live === true;

  const send = async () => {
    if (!pick || !nSel || sent || sending) return;
    if (!canWrite) return;
    setSending(true);
    try {
      const res = await salesPackSend(client, { body: { facilityId: pick.facilityId, docs: share.filter((d) => sel[d.doc]).map((d) => d.doc) } });
      if (res.connect_email) {
        setTab('CRM', { mxOpen: '1' });
        return;
      }
      setSendAns({ facilityId: pick.facilityId, res });
    } catch (e) {
      setSendAns({ facilityId: pick.facilityId, err: e as ApiError });
    } finally {
      setSending(false);
    }
  };
  const answer = pick && sendAns?.facilityId === pick.facilityId ? sendAns : null;

  // ── Library ──
  const docSel = params.get('slDoc') && kit.some((d) => d.doc === params.get('slDoc')) ? params.get('slDoc')! : (kit[0]?.doc ?? '');
  const doc = kit.find((d) => d.doc === docSel) ?? null;
  const [linkCopied, setLinkCopied] = useState<string | null>(null);
  const [pzQuery, setPzQuery] = useState('');
  const [pzPick, setPzPick] = useState<CatalogRow | null>(null);
  const pzt = pzQuery.trim();
  const [pzDeb, setPzDeb] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setPzDeb(pzt), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [pzt]);
  const pzQ = useQuery((signal) => crmFacilitySearch(client, { body: { filters: /^[A-Za-z]{2}$/.test(pzDeb) ? { state: [pzDeb.toUpperCase()] } : { q: pzDeb }, sort: { by: 'name', dir: 'asc' }, limit: 5 }, signal }), [client, pzDeb], {
    enabled: mode === 'lib' && !!doc?.personalize && !!pzDeb && !pzPick,
  });
  const proofLine = usePilotProofLine(mode === 'lib');

  const print = () => {
    if (typeof window !== 'undefined') window.print();
  };
  const sendDisabled = !canWrite;

  const v = {
    kitCount: kit.length + ' resources',
    kitModes: MODES.map((m) => ({ name: m[1], on: mode === m[0], onClick: () => setParam('kitMode', m[0] === 'call' ? null : m[0]), style: seg(mode === m[0]) })),
    kitIsCall: mode === 'call',
    kitIsObj: mode === 'obj',
    kitIsSend: mode === 'send',
    kitIsLib: mode === 'lib',
    kitQ: q,
    kitSetQ: (e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value),
    kitQClear: () => setQ(''),
    kitQHas: !!qt,
    kitChips: CHIPS.map((c) => ({
      name: c,
      onClick: () => setQ(c),
      style:
        'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:13px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' +
        (q === c ? 'background:rgba(79,138,214,.18);color:#264C7E;' : 'background:rgba(34,39,46,.05);color:#3C454F;'),
    })),
    kitHitHead: qt
      ? searching
        ? 'Searching the kit for “' + qt + '”'
        : hits.length
          ? hits.length + (hits.length === 1 ? ' answer' : ' answers') + ' for “' + qt + '”'
          : 'Nothing in the kit matches “' + qt + '”'
      : 'Lines to say, then the key points · search for anything else',
    kitHitNone: !!qt && !searching && !searchQ.error && !hits.length,
    kitHits: hits.map((it, i) => {
      const k = 'h' + i + it.label;
      const on = copied === k;
      const s = sentences(it.text);
      return {
        label: it.say ? it.label : it.sec,
        doc: it.doc,
        lead: s[0] || '',
        pts: s.slice(1, 4).map((x) => ({ t: x })),
        hasPts: s.length > 1,
        copyLabel: on ? 'Copied' : 'Copy',
        copyStyle: cbtn(on),
        onCopy: () => copy(k, it.text),
        onOpen: () => {
          setParam('kitMode', 'lib');
          setParam('slDoc', it.doc);
        },
        enter: enter(i),
      };
    }),
    kitObjCount:
      objs.filter((o) => !o.stall).length +
      ' objections · ' +
      objs.filter((o) => o.stall).length +
      ' stall points' +
      (objs.length ? ' · from ' + sourceList(objs.map((o) => o.doc)) : ''),
    kitObjs: objs.map((o, i) => {
      const k = 'o' + i;
      const on = copied === k;
      return {
        they: o.they,
        you: o.you,
        isStall: o.stall,
        proof: proofOf(o.you),
        doc: o.doc,
        copyLabel: on ? 'Copied' : 'Copy',
        copyStyle: cbtn(on),
        onCopy: () => copy(k, o.you),
        onOpen: () => {
          setParam('kitMode', 'lib');
          setParam('slDoc', o.doc);
        },
        enter: enter(i),
      };
    }),
    kitSendQ: sendQ,
    kitSendSetQ: (e: ChangeEvent<HTMLInputElement>) => {
      setSendQ(e.target.value);
      setPick(null);
    },
    kitSugHas: !pick,
    kitSugHead: prHits.length
      ? pq
        ? prHits.length + ' CRM facilities match'
        : 'CRM priority facilities · sending starts the deal if it isn’t open'
      : prosQ.loading
        ? 'Loading your CRM priority facilities'
        : pq
          ? 'No CRM facility matches “' + sendQ.trim() + '”' + (countsQ.data ? ' · search all ' + roundedCount(countsQ.data.catalog) + ' in CRM' : '')
          : 'No CRM priority facilities yet',
    kitSug: prHits.slice(0, 10).map((p) => ({
      // The name as Targeting and the CRM show it: as stored, only an all-capitals catalog name is softened.
      label: tc(p.name ?? '–'),
      meta: profileOf(p),
      onClick: () => {
        setPick(p);
        setSendQ('');
      },
    })),
    kitPickHas: !!pick,
    kitPickFields: pick
      ? [
          ['Facility', pick.name ?? '–'],
          ['Profile', profileOf(pick)],
          ['Why now', pick.talkingPoint || '–'],
          ['In CRM', pick.tracked && pick.status === 'open' ? 'Deal open' : 'Not started · sending starts it'],
        ].map((r) => ({ label: r[0], val: r[1] }))
      : [],
    kitPickClear: () => setPick(null),
    kitPackCount: nSel + ' of ' + share.length + ' selected',
    kitPack: share.map((d) => ({
      name: d.doc,
      box:
        'width:16px;height:16px;flex:0 0 16px;border-radius:4px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:#fff;' +
        (sel[d.doc] ? 'background:#2F5C97;' : 'border:1.5px solid rgba(34,39,46,.35);'),
      mark: sel[d.doc] ? '✓' : '',
      on: !!sel[d.doc],
      note: d.personalize ? 'Filled from this facility’s record' : 'Shareable as is',
      onClick: () => setPackSel({ ...sel, [d.doc]: !sel[d.doc] }),
    })),
    kitPackNone: !pick,
    kitPackHas: !!pick,
    kitPackFor: pick ? 'Prospect pack for ' + (pick.name ?? '') : '',
    kitSendLabel: sent ? 'Sent · logged to the deal' : sending ? 'Sending…' : connected ? 'Send from my inbox' : 'Connect email to send',
    kitSendStyle:
      'display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (sent
        ? 'background:rgba(47,138,111,.16);color:#12503C;'
        : nSel && !sendDisabled
          ? 'background:#2F5C97;color:#fff;'
          : 'background:rgba(34,39,46,.08);color:#54606C;' + (nSel ? 'cursor:not-allowed;' : 'pointer-events:none;')),
    kitSend: () => void send(),
    kitSentHas: sent,
    kitOpenDeal: () => {
      if (!pick) return;
      openDeal(answer?.res?.deal_id ? { dealId: answer.res.deal_id } : { facilityId: pick.facilityId });
    },
    kitSendNote: sent
      ? 'Sent from your inbox. Logged to the deal’s history in CRM.'
      : answer?.res?.reason
        ? answer.res.reason
        : connected
          ? 'Sends from your own mailbox, CC partner@. Logged to the deal’s history.'
          : 'Your inbox isn’t connected. Connect it in CRM, or open the One-Pager and use Print / PDF.',
    kitPrint: print,
    kitPrintStyle: btn(false),
    kitDocs: kit.map((d, di) => ({
      name: d.doc,
      chip: d.chip,
      note: d.note,
      onClick: () => setParam('slDoc', d.doc),
      chipStyle: chipStyle(d.chip),
      wrap:
        enter(di, 30, 260, '.28s') +
        'display:block;text-decoration:none;color:inherit;border-radius:9px;padding:10px 12px;margin-bottom:6px;' +
        (docSel === d.doc ? 'background:rgba(79,138,214,.09);box-shadow:inset 3px 0 0 #2F5C97;' : ''),
    })),
    kitOpenMp: () => openPrep(null),
    kitCopyAll: () => {
      if (!doc) return;
      copyText(docText(doc));
      setCopied('ALL:' + doc.doc);
    },
    kitCopyAllLabel: doc && copied === 'ALL:' + doc.doc ? 'Copied' : 'Copy all',
    kitCopyAllStyle: btn(false),
    kitTitle: docSel,
    kitSub: doc?.sub ?? '',
    kitHasLink: !!doc?.link,
    kitLink: doc?.link ?? '',
    kitLinkShort: (doc?.link ?? '').replace(/^https:\/\//, ''),
    kitLinkNote: 'Send the link, not a PDF - the hosted copy is always the current version and it tells you who opened it.',
    kitCopyLink: () => {
      if (!doc?.link) return;
      copyText(doc.link);
      setLinkCopied(doc.doc);
    },
    kitCopyLinkLabel: linkCopied === docSel ? 'Link copied' : 'Copy link',
    kitIsOnePager: !!doc?.personalize,
    kitPzQuery: pzQuery,
    kitPzSetQuery: (e: ChangeEvent<HTMLInputElement>) => setPzQuery(e.target.value),
    kitPzResults:
      !pzt || pzPick || pzDeb !== pzt
        ? []
        : (pzQ.data?.rows ?? []).slice(0, 5).map((f) => ({
            label: f.name ?? '–',
            meta: [f.city, f.state].filter(Boolean).join(', ') + (f.beds != null ? ' · ' + f.beds + ' beds' : ''),
            onClick: () => {
              setPzPick(f);
              setPzQuery(f.name ?? '');
            },
            style: 'display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;padding:8px 11px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.07);',
          })),
    kitPzChosen: !!pzPick,
    kitPzClear: () => {
      setPzPick(null);
      setPzQuery('');
    },
    kitPzFields: pzPick ? pzFields(pzPick) : [],
    kitPzLead: pzPick ? 'Prepared for ' + (pzPick.name ?? '') + ' · ' + [pzPick.city, pzPick.state].filter(Boolean).join(', ') + (pzPick.beds != null ? ' · ' + pzPick.beds + ' beds' : '') : '',
    kitPzNote: 'Pulled from the catalog and the facility’s peer score. Nothing here is typed by hand, so it cannot go stale between the meeting and the leave-behind.',
    kitSections: (doc?.sections ?? []).map((sec, si) => ({
      enter: enter(si, 34, 280, '.3s'),
      hasStep: !!sec.step,
      step: sec.step,
      name: sec.name,
      body: sec.body,
      quotes: sec.quotes.map((qq, qi) => {
        const k = 'q' + docSel + si + ':' + qi;
        return { label: qq[0], text: qq[1], onCopy: () => copy(k, qq[1]), copyLabel: copied === k ? 'Copied' : 'Copy', copyStyle: qbtn(copied === k) };
      }),
      bullets: sec.bullets,
      hasFlag: !!sec.flag,
      flag: sec.flag,
      objections: sec.objections.map((o) => ({ q: o[0], a: o[1] })),
    })),
    kitProofLine: proofLine,
  };

  const modeRow = (
    <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;margin-bottom:10px;')}>
      {v.kitModes.map((m, m_i) => (
        <Fragment key={m_i}>
          <a href="#" role="tab" aria-selected={m.on} onClick={ck(m.onClick)} style={sx(m.style)}>
            <span>{m.name}</span>
          </a>
        </Fragment>
      ))}{' '}
      <span style={sx('flex:1;')} />{' '}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="1.9" strokeLinecap="round" style={sx('flex:0 0 13px;')}>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" />
      </svg>{' '}
      <span style={sx('font-size:14px;color:#54606C;')}>
        confidential &middot; <span>{kitQ.data ? v.kitCount : '–'}</span>
      </span>
    </div>
  );

  if (!kitQ.data) {
    return (
      <div data-testid="sales-kit">
        {modeRow}
        {kitQ.error ? <ErrorNote error={kitQ.error} onRetry={kitQ.reload} /> : <Skeleton rows={6} label="Loading the Sales Kit" />}
      </div>
    );
  }

  return (
    <div data-testid="sales-kit" style={{ display: 'contents' }}>
      {modeRow}{' '}
      {!!v.kitIsCall && (
        <>
          <div data-testid="kit-call" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);min-width:0;height:calc(100vh - 222px);overflow-y:auto;overflow-x:hidden;padding:12px 2px 0;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;height:42px;padding:0 13px;border-radius:10px;background:rgba(34,39,46,.05);')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="2" strokeLinecap="round" style={sx('flex:0 0 16px;')}>
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>{' '}
              <input
                value={v.kitQ}
                onChange={v.kitSetQ}
                aria-label="Search the Sales Kit"
                placeholder="What did they just ask? Try: cost, HIPAA, what is the catch"
                style={sx('flex:1 1 auto;min-width:0;border:0;outline:0;background:transparent;font-family:inherit;font-size:16px;color:#22272E;')}
              />{' '}
              {!!v.kitQHas && (
                <a href="#" onClick={ck(v.kitQClear)} style={sx('font-size:14px;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                  Clear
                </a>
              )}
            </div>{' '}
            <div style={sx('display:flex;gap:6px;flex-wrap:wrap;margin-top:9px;')}>
              {v.kitChips.map((c, c_i) => (
                <Fragment key={c_i}>
                  <a href="#" onClick={ck(c.onClick)} style={sx(c.style)}>
                    <span>{c.name}</span>
                  </a>
                </Fragment>
              ))}
            </div>{' '}
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:14px 0 4px;')}>
              <span>{v.kitHitHead}</span>
            </div>{' '}
            {!!searchQ.error && !!qt && <ErrorNote error={searchQ.error} onRetry={searchQ.reload} compact />}
            {!!v.kitHitNone && (
              <div style={sx('font-size:16px;color:#3C454F;padding:10px 0;line-height:1.55;')}>
                Try a shorter word, or open the Library. Ask Help (?) for anything outside the Sales Kit.
              </div>
            )}{' '}
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));column-gap:22px;')}>
              {v.kitHits.map((h, h_i) => (
                <Fragment key={h_i}>
                  <div style={sx(`${h.enter}border-top:1px solid rgba(34,39,46,.1);padding:10px 0 11px;min-width:0;`)}>
                    <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:4px;margin-bottom:5px;')}>
                      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.07em;color:#2F5C97;min-width:0;overflow-wrap:anywhere;')}>
                        <span>{h.label}</span>
                      </span>{' '}
                      <span style={sx('flex:1;')} />{' '}
                      <a href="#" onClick={ck(h.onCopy)} style={sx(h.copyStyle)}>
                        <span>{h.copyLabel}</span>
                      </a>
                    </div>{' '}
                    <div style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.45;text-wrap:pretty;')}>
                      <span translate="no">{h.lead}</span>
                    </div>{' '}
                    {!!h.hasPts && (
                      <div style={sx('display:flex;flex-direction:column;gap:3px;margin-top:5px;')}>
                        {' '}
                        {h.pts.map((p, p_i) => (
                          <Fragment key={p_i}>
                            <div style={sx('display:flex;gap:7px;font-size:16px;color:#3C454F;line-height:1.45;min-width:0;')}>
                              <span style={sx('flex:0 0 5px;height:5px;border-radius:50%;background:#4F8AD6;margin-top:7px;')} />
                              <span style={sx('min-width:0;text-wrap:pretty;')}>
                                <span translate="no">{p.t}</span>
                              </span>
                            </div>
                          </Fragment>
                        ))}{' '}
                      </div>
                    )}{' '}
                    <a href="#" onClick={ck(h.onOpen)} style={sx('display:inline-block;margin-top:5px;font-size:14px;color:#54606C;text-decoration:none;')} className={hov('color:#2F5C97;')}>
                      <span>{h.doc}</span> &rarr;
                    </a>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </>
      )}{' '}
      {!!v.kitIsObj && (
        <>
          <div data-testid="kit-obj" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);min-width:0;height:calc(100vh - 222px);overflow-y:auto;overflow-x:hidden;padding:6px 2px 0;')}>
            <div style={sx('font-size:14px;color:#54606C;padding:4px 0 2px;')}>
              <span>{v.kitObjCount}</span>
            </div>{' '}
            <div style={sx('display:grid;grid-template-columns:minmax(0,.9fr) minmax(0,2fr) minmax(0,.8fr) 64px;gap:14px;padding:6px 0;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
              <span>They say</span>
              <span>You say</span>
              <span>Proof</span>
              <span />
            </div>{' '}
            {v.kitObjs.map((o, o_i) => (
              <Fragment key={o_i}>
                <div style={sx(`${o.enter}display:grid;grid-template-columns:minmax(0,.9fr) minmax(0,2fr) minmax(0,.8fr) 64px;gap:14px;align-items:start;border-top:1px solid rgba(34,39,46,.1);padding:10px 0;`)}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.4;')}>
                    <span translate="no">{o.they}</span>
                    {!!o.isStall && <span style={sx('display:block;margin-top:4px;font-size:14px;font-weight:800;letter-spacing:.08em;color:#54606C;')}>STALL POINT</span>}
                  </span>{' '}
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.55;text-wrap:pretty;')}>
                    <span translate="no">{o.you}</span>
                    <a href="#" onClick={ck(o.onOpen)} style={sx('display:block;margin-top:4px;font-size:14px;color:#54606C;text-decoration:none;')}>
                      <span>{o.doc}</span> &rarr;
                    </a>
                  </span>{' '}
                  <span style={sx('font-size:16px;font-weight:700;color:#264C7E;line-height:1.4;font-variant-numeric:tabular-nums;')}>
                    <span translate="no">{o.proof}</span>
                  </span>{' '}
                  <a href="#" onClick={ck(o.onCopy)} style={sx(o.copyStyle)}>
                    <span>{o.copyLabel}</span>
                  </a>
                </div>
              </Fragment>
            ))}
          </div>
        </>
      )}{' '}
      {!!v.kitIsSend && (
        <>
          <div
            data-testid="kit-send"
            style={sx(
              'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);min-width:0;height:calc(100vh - 222px);overflow-y:auto;overflow-x:hidden;padding:12px 2px 0;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:24px;align-items:start;',
            )}
          >
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:8px;')}>1 &middot; Which facility</div>{' '}
              <input
                value={v.kitSendQ}
                onChange={v.kitSendSetQ}
                aria-label="Search your CRM facilities"
                placeholder="Search your CRM facilities by name or state"
                style={sx('box-sizing:border-box;width:100%;height:38px;padding:0 12px;border-radius:9px;border:1px solid rgba(34,39,46,.18);background:rgba(34,39,46,.03);font-family:inherit;font-size:16px;color:#22272E;outline:0;')}
              />{' '}
              {!!prosQ.error && <ErrorNote error={prosQ.error} onRetry={prosQ.reload} compact />}
              {!!v.kitSugHas && (
                <div style={sx('margin-top:10px;')}>
                  <div style={sx('font-size:14px;color:#54606C;margin-bottom:4px;')}>
                    <span>{v.kitSugHead}</span>
                  </div>{' '}
                  {v.kitSug.map((g, g_i) => (
                    <Fragment key={g_i}>
                      <a href="#" onClick={ck(g.onClick)} style={sx('display:flex;align-items:center;gap:9px;padding:8px 2px;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:#22272E;')} className={hov('background:rgba(79,138,214,.07);')}>
                        <span style={sx('font-size:16px;font-weight:700;min-width:0;')}>
                          <span>{g.label}</span>
                        </span>
                        <span style={sx('flex:1;')} />
                        <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                          <span>{g.meta}</span>
                        </span>
                      </a>
                    </Fragment>
                  ))}
                </div>
              )}{' '}
              {!!v.kitPickHas && (
                <div style={sx('margin-top:10px;')}>
                  {v.kitPickFields.map((p, p_i) => (
                    <Fragment key={p_i}>
                      <div style={sx('display:flex;gap:10px;font-size:16px;padding:6px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                        <span style={sx('flex:0 0 110px;color:#54606C;')}>
                          <span>{p.label}</span>
                        </span>
                        <span style={sx('min-width:0;font-weight:600;')}>
                          <span>{p.val}</span>
                        </span>
                      </div>
                    </Fragment>
                  ))}{' '}
                  <a href="#" onClick={ck(v.kitPickClear)} style={sx('display:inline-block;margin-top:6px;font-size:14px;color:#2F5C97;text-decoration:none;')}>
                    Pick another facility
                  </a>
                </div>
              )}
            </div>{' '}
            <div style={sx('min-width:0;')}>
              <div style={sx('display:flex;align-items:center;gap:8px;margin-bottom:8px;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>2 &middot; What to send</span>
                <span style={sx('flex:1;')} />
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{v.kitPackCount}</span>
                </span>
              </div>{' '}
              {v.kitPack.map((k, k_i) => (
                <Fragment key={k_i}>
                  <a href="#" role="checkbox" aria-checked={k.on} onClick={ck(k.onClick)} style={sx('display:flex;align-items:center;gap:10px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:#22272E;')}>
                    <span style={sx(k.box)}>
                      <span>{k.mark}</span>
                    </span>{' '}
                    <span style={sx('font-size:16px;font-weight:700;min-width:0;')}>
                      <span>{k.name}</span>
                    </span>{' '}
                    <span style={sx('flex:1;')} />{' '}
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{k.note}</span>
                    </span>
                  </a>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:16px 0 8px;')}>3 &middot; Send</div>{' '}
              {!!v.kitPackNone && <div style={sx('font-size:16px;color:#54606C;')}>Pick a facility first.</div>}{' '}
              {!!v.kitPackHas && (
                <div>
                  <div style={sx('font-size:16px;font-weight:700;margin-bottom:8px;')}>
                    <span>{v.kitPackFor}</span>
                  </div>{' '}
                  <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
                    <a
                      href="#"
                      role="button"
                      aria-disabled={sendDisabled || !nSel || undefined}
                      title={sendDisabled ? (writeReason ?? undefined) : undefined}
                      onClick={ck(v.kitSend)}
                      style={sx(v.kitSendStyle)}
                    >
                      <span>{v.kitSendLabel}</span>
                    </a>
                    <a href="#" onClick={ck(v.kitPrint)} style={sx(v.kitPrintStyle)}>
                      Print / PDF
                    </a>
                    {!!v.kitSentHas && (
                      <a href="#" onClick={ck(v.kitOpenDeal)} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#2F5C97;background:rgba(79,138,214,.12);')}>
                        Open the deal in CRM &rarr;
                      </a>
                    )}
                  </div>{' '}
                  {answer?.err ? (
                    <div style={sx('margin-top:8px;')}>
                      <ErrorNote error={answer.err} compact />
                    </div>
                  ) : (
                    <div data-testid="kit-send-note" style={sx('font-size:14px;color:#54606C;margin-top:8px;line-height:1.5;')}>
                      <span>{v.kitSendNote}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}{' '}
      {!!v.kitIsLib && (
        <>
          <div data-testid="kit-lib" style={sx('display:grid;grid-template-columns:250px minmax(0,1fr);gap:14px;align-items:start;')}>
            <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 13px;min-width:0;max-height:calc(100vh - 222px);overflow-y:auto;overflow-x:hidden;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Resources</div>{' '}
              {v.kitDocs.map((d, d_i) => (
                <Fragment key={d_i}>
                  <a href="#" onClick={ck(d.onClick)} aria-current={d.name === docSel || undefined} style={sx(d.wrap)}>
                    <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;')}>
                        <span>{d.name}</span>
                      </span>{' '}
                      <span style={sx('flex:1;')} />{' '}
                      <span style={sx(d.chipStyle)}>
                        <span>{d.chip}</span>
                      </span>
                    </span>{' '}
                    <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.5;margin-top:5px;')}>
                      <span>{d.note}</span>
                    </span>
                  </a>
                </Fragment>
              ))}
            </div>{' '}
            <div data-testid="kit-doc" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;max-height:calc(100vh - 222px);overflow-y:auto;overflow-x:hidden;')}>
              <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:13px;')}>
                <img src="/aithora-mark.svg" alt="Aithora" style={sx('width:22px;height:22px;flex:0 0 22px;object-fit:contain;')} />{' '}
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Sales kit</span>{' '}
                <span style={sx('flex:1;')} />{' '}
                <a href="#" onClick={ck(v.kitOpenMp)} style={sx('display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;')}>
                  Prep for a meeting
                </a>{' '}
                <a href="#" onClick={ck(v.kitCopyAll)} style={sx(v.kitCopyAllStyle)}>
                  <span>{v.kitCopyAllLabel}</span>
                </a>
              </div>{' '}
              <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.01em;')}>
                <span>{v.kitTitle}</span>
              </div>{' '}
              <div style={sx('font-size:16px;color:#54606C;margin-top:4px;margin-bottom:16px;')}>
                <span>{v.kitSub}</span>
              </div>{' '}
              {!!v.kitHasLink && (
                <div data-testid="kit-link" style={sx('box-sizing:border-box;background:rgba(47,138,111,.08);border:1px solid rgba(47,138,111,.3);border-radius:10px;padding:12px 14px;margin-bottom:16px;')}>
                  <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:8px;')}>
                    <a href={v.kitLink} target="_blank" rel="noopener" style={sx('display:inline-flex;align-items:center;gap:7px;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#1F6B52;color:#fff;')}>
                      Open the document &#8599;
                    </a>{' '}
                    <a href="#" onClick={ck(v.kitCopyLink)} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);')}>
                      <span>{v.kitCopyLinkLabel}</span>
                    </a>{' '}
                    <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
                      <span>{v.kitLinkShort}</span>
                    </span>
                  </div>{' '}
                  <div style={sx('font-size:14px;color:#54606C;margin-top:8px;line-height:1.45;text-wrap:pretty;')}>
                    <span>{v.kitLinkNote}</span>
                  </div>
                </div>
              )}{' '}
              {!!v.kitIsOnePager && (
                <div data-testid="kit-personalize" style={sx('box-sizing:border-box;padding:12px 14px;margin-bottom:16px;')}>
                  <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-bottom:9px;')}>
                    <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Personalize for this meeting</span>{' '}
                    <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />{' '}
                    <a href="#" onClick={ck(v.kitPrint)} style={sx(v.kitPrintStyle)}>
                      Print / PDF
                    </a>
                  </div>{' '}
                  {!!v.kitPzChosen && (
                    <div>
                      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:10px;')}>
                        <span style={sx('font-size:16px;font-weight:700;')}>
                          <span>{v.kitPzLead}</span>
                        </span>{' '}
                        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />{' '}
                        <a href="#" onClick={ck(v.kitPzClear)} style={sx('font-size:14px;color:#2F5C97;text-decoration:none;')}>
                          Clear
                        </a>
                      </div>{' '}
                      {v.kitPzFields.map((p, p_i) => (
                        <Fragment key={p_i}>
                          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;font-size:16px;padding:6px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                            <span style={sx('flex:0 1 auto;min-width:0;color:#54606C;width:140px;')}>
                              <span>{p.label}</span>
                            </span>{' '}
                            <span style={sx('flex:1 1 130px;min-width:min(100%,130px);color:#22272E;font-weight:600;')}>
                              <span>{p.val}</span>
                            </span>
                          </div>
                        </Fragment>
                      ))}{' '}
                      <div style={sx('font-size:14px;color:#54606C;margin-top:9px;max-width:78ch;text-wrap:pretty;')}>
                        <span>{v.kitPzNote}</span>
                      </div>
                    </div>
                  )}{' '}
                  {!!v.kitPzChosen && <div />}{' '}
                  <input
                    type="text"
                    value={v.kitPzQuery}
                    onChange={v.kitPzSetQuery}
                    aria-label="Search the catalog"
                    placeholder="Search the catalog by facility, city or state&hellip;"
                    style={sx('box-sizing:border-box;width:100%;height:26px;padding:0 12px;border-top:1px solid rgba(34,39,46,.14);font-size:16px;color:#22272E;')}
                  />{' '}
                  {!!pzQ.error && !!pzt && <ErrorNote error={pzQ.error} onRetry={pzQ.reload} compact />}
                  {v.kitPzResults.map((r, r_i) => (
                    <Fragment key={r_i}>
                      <a href="#" onClick={ck(r.onClick)} style={sx(r.style)} className={hov('background:rgba(79,138,214,.08);')}>
                        <span style={sx('font-size:16px;font-weight:600;')}>
                          <span>{r.label}</span>
                        </span>{' '}
                        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />{' '}
                        <span style={sx('font-size:14px;color:#54606C;')}>
                          <span>{r.meta}</span>
                        </span>
                      </a>
                    </Fragment>
                  ))}
                </div>
              )}{' '}
              {v.kitSections.map((sec, sec_i) => (
                <Fragment key={sec_i}>
                  <div style={sx(`${sec.enter}margin-bottom:18px;`)}>
                    {!!sec.hasStep && (
                      <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.1em;color:#2F5C97;margin-bottom:5px;')}>
                        <span>{sec.step}</span>
                      </div>
                    )}{' '}
                    <div style={sx('font-size:16px;font-weight:700;margin-bottom:6px;')}>
                      <span>{sec.name}</span>
                    </div>{' '}
                    <div style={sx('font-size:16px;color:#3C454F;line-height:1.65;')}>
                      <span translate="no">{sec.body}</span>
                    </div>{' '}
                    {sec.quotes.map((qq, q_i) => (
                      <Fragment key={q_i}>
                        <div style={sx('border-left:3px solid rgba(47,92,151,.4);background:rgba(79,138,214,.06);border-radius:0 9px 9px 0;padding:11px 13px;margin-top:11px;')}>
                          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-bottom:7px;')}>
                            <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;color:#2F5C97;min-width:0;overflow-wrap:anywhere;')}>
                              <span>{qq.label}</span>
                            </span>{' '}
                            <span style={sx('flex:1;')} />{' '}
                            <a href="#" onClick={ck(qq.onCopy)} style={sx(qq.copyStyle)}>
                              <span>{qq.copyLabel}</span>
                            </a>
                          </div>{' '}
                          <div style={sx('font-size:16px;color:#22272E;line-height:1.65;')}>
                            <span translate="no">{qq.text}</span>
                          </div>
                        </div>
                      </Fragment>
                    ))}{' '}
                    {sec.bullets.map((b, b_i) => (
                      <Fragment key={b_i}>
                        <div style={sx('display:flex;gap:10px;margin-top:7px;')}>
                          <span style={sx('width:5px;height:5px;border-radius:50%;background:#54606C;margin-top:8px;flex:0 0 5px;')} />{' '}
                          <span style={sx('font-size:16px;color:#3C454F;line-height:1.6;')}>
                            <span translate="no">{b}</span>
                          </span>
                        </div>
                      </Fragment>
                    ))}{' '}
                    {!!sec.hasFlag && (
                      <div style={sx('display:flex;align-items:flex-start;gap:9px;background:rgba(91,107,124,.08);border:1px solid rgba(91,107,124,.26);border-radius:9px;padding:10px 12px;margin-top:12px;')}>
                        <span style={sx('color:#5B6B7C;font-size:16px;flex:0 1 auto;min-width:0;')}>&#9873;</span>{' '}
                        <span style={sx('font-size:16px;color:#3C454F;line-height:1.6;')}>
                          <span translate="no">{sec.flag}</span>
                        </span>
                      </div>
                    )}{' '}
                    {sec.objections.map((o, o_i) => (
                      <Fragment key={o_i}>
                        <div style={sx('border:1px solid rgba(34,39,46,.12);border-radius:9px;padding:10px 12px;margin-top:9px;background:#FBF8F1;')}>
                          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
                            <span translate="no">{o.q}</span>
                          </div>{' '}
                          <div style={sx('font-size:16px;color:#3C454F;line-height:1.6;margin-top:5px;')}>
                            <span translate="no">{o.a}</span>
                          </div>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </Fragment>
              ))}{' '}
              <div data-testid="kit-proof-line" style={sx('font-size:14px;color:#54606C;line-height:1.4;padding-top:4px;margin-top:3px;border-top:1px solid rgba(34,39,46,.11);min-width:0;text-wrap:pretty;')}>
                <span>{v.kitProofLine}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
